import React from 'react'
import {log} from '@/logger'

import {api} from '../services/api'
import {AlbumTagStats, ResourceID} from '../types'

// Matches the codebase-wide fetch-status vocabulary used by sibling hooks
// ('pending'/'rejected' rather than 'loading'/'error').
export type FetchStatus = 'idle' | 'pending' | 'resolved' | 'rejected'

interface Params {
  clientId: ResourceID
  albumId: ResourceID
  // Gate the fetch so the (employee-only, flag-gated) call never fires for a
  // non-employee or when the release flag is off.
  enabled: boolean
}

export interface AlbumTagStatsState {
  status: FetchStatus
  data: AlbumTagStats | null
}

/**
 * Owns the album tag-stats fetch for a single album. Best-effort and silent: a
 * failure (including the legitimate 403 a non-staff caller receives) resolves to
 * the 'rejected' state with a breadcrumb and no data, so the consuming UI simply
 * renders nothing. A `cancelled` flag stale-guards the state set so a response
 * landing after unmount / dependency change is discarded.
 */
export const useAlbumTagStats = ({
  clientId,
  albumId,
  enabled,
}: Params): AlbumTagStatsState => {
  const [status, setStatus] = React.useState<FetchStatus>('idle')
  const [data, setData] = React.useState<AlbumTagStats | null>(null)

  React.useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    setStatus('pending')

    const fetchData = async (): Promise<void> => {
      const response = await api.photos.getAlbumTagStats(clientId, albumId)

      if (cancelled) return

      if (response.result !== 'success') {
        setData(null)
        setStatus('rejected')
        log.breadcrumb('Failed to load album tag stats', {albumId})
        return
      }

      setData(response.data)
      setStatus('resolved')
    }

    fetchData()

    return (): void => {
      cancelled = true
    }
  }, [enabled, clientId, albumId])

  return {status, data}
}
